import { Expand, Minimize2, Minus, Plus } from "lucide-react";

import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import {
  CONTROLS_DEFAULT,
  CONTROLS_MIN,
  CONTROLS_STEP,
  getBaseZoom,
  getViewportCenterNorm,
  isImageFile,
  ZOOM_STEP,
  zoomAroundPoint,
} from "@/features/request/feedback-tool/feedbackTool/utils";

export function ZoomControls() {
  const {
    applyZoom,
    controls,
    divImgRef,
    imageOnly,
    img,
    imgRef,
    isTablet,
    pdfContainerRef,
    pdfScrollerRef,
    setImageOnly,
    setSize,
    size,
    videoRef,
    zoom,
  } = useFeedbackTool();
  const isImage = isImageFile(img.url);
  const isPdf = img.url.includes(".pdf");
  const showZoomControls = isImage || isPdf;
  const video = videoRef.current;

  const applyPdfCenteredZoom = (newZoom: number, newControls: number) => {
    const content = pdfContainerRef.current;
    const hScroller = pdfScrollerRef.current;
    const vScroller = divImgRef.current;

    if (!content || !hScroller || !vScroller) return;

    const { normX, normY } = getViewportCenterNorm(
      content,
      hScroller,
      vScroller,
    );

    zoomAroundPoint(
      content,
      hScroller,
      vScroller,
      normX,
      normY,
      newZoom,
      newControls,
      applyZoom,
    );
  };

  const zoomBy = (newZoom: number, newControls: number) => {
    if (isPdf) {
      applyPdfCenteredZoom(newZoom, newControls);

      return;
    }

    applyZoom(newZoom, newControls);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sliderVal = Number(e.target.value);

    if (!Number.isFinite(sliderVal)) return;

    const newControls = CONTROLS_DEFAULT + CONTROLS_MIN - sliderVal;
    const baseZoom = getBaseZoom(size, controls, imgRef.current, zoom);
    const zoomDelta = (controls - newControls) * (ZOOM_STEP / CONTROLS_STEP);

    zoomBy(baseZoom + zoomDelta, newControls);
  };

  const handleExpand = () => {
    if (size === "MODAL") setSize("FULL");
    setImageOnly(true);
  };

  if (isTablet) {
    return (
      <div className="absolute flex bottom-4 left-1/2 -translate-x-1/2 z-20">
        {showZoomControls ? (
          <div className="flex items-center">
            <Minus
              className="text-slate-500 cursor-pointer mr-4"
              onClick={() => {
                if (controls >= CONTROLS_DEFAULT) return;
                zoomBy(zoom - ZOOM_STEP, controls + CONTROLS_STEP);
              }}
              size={24}
            />
            <input
              className="w-30 h-3 cursor-pointer accent-[#6938EF]"
              max={CONTROLS_DEFAULT}
              min={CONTROLS_MIN}
              onChange={handleSliderChange}
              step={CONTROLS_STEP}
              type="range"
              value={CONTROLS_DEFAULT + CONTROLS_MIN - controls}
            />
            <Plus
              className="text-slate-500 cursor-pointer ml-4 mr-4"
              onClick={() => {
                if (controls <= CONTROLS_MIN) return;
                zoomBy(zoom + ZOOM_STEP, controls - CONTROLS_STEP);
              }}
              size={24}
            />
          </div>
        ) : null}
        {video || isPdf ? null : imageOnly ? (
          <Minimize2
            className="text-slate-500 cursor-pointer"
            onClick={() => setImageOnly(false)}
            size={24}
          />
        ) : (
          <Expand
            className="text-slate-500 cursor-pointer"
            onClick={handleExpand}
            size={24}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`hidden absolute sm:flex ${size === "MODAL" ? "bottom-8 right-95" : "bottom-25 right-108"} z-20`}
    >
      {size === "FULL" && showZoomControls ? (
        <div className="relative flex items-center">
          <Minus
            className="text-slate-500 cursor-pointer mr-4"
            onClick={() => {
              if (controls >= CONTROLS_DEFAULT) return;
              zoomBy(zoom - ZOOM_STEP, controls + CONTROLS_STEP);
            }}
            size={24}
          />
          <input
            className="w-30 h-3 cursor-pointer appearance-none bg-slate-500 rounded-sm
              [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-utility-brand-600 [&::-webkit-slider-thumb]:bg-white
              [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-4 [&::-moz-range-thumb]:border-utility-brand-600 [&::-moz-range-thumb]:bg-white
              [&::-moz-range-track]:h-3 [&::-moz-range-track]:rounded-sm [&::-moz-range-track]:bg-slate-500 [&::-moz-range-track]:border-none"
            max={CONTROLS_DEFAULT}
            min={CONTROLS_MIN}
            onChange={handleSliderChange}
            step={CONTROLS_STEP}
            type="range"
            value={CONTROLS_DEFAULT + CONTROLS_MIN - controls}
          />
          <Plus
            className="text-slate-500 cursor-pointer ml-4 mr-4"
            onClick={() => {
              if (controls <= CONTROLS_MIN) return;
              zoomBy(zoom + ZOOM_STEP, controls - CONTROLS_STEP);
            }}
            size={24}
          />
        </div>
      ) : null}
      {video || isPdf ? null : (
        <Expand
          className="text-slate-500 cursor-pointer"
          onClick={() => {
            if (size === "MODAL") {
              setSize("FULL");
            } else {
              setSize("MODAL");
            }
          }}
          size={24}
        />
      )}
    </div>
  );
}
